import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {BackendResponse} from '@common/http/backend-response/backend-response';

export interface BlogHeading {
  id: string;
  text: string;
}

export interface BlogCatalogPost {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  cover_image: string | null;
  category: string | null;
  category_title: string | null;
  published_at: string | null;
}

export interface BlogCatalogCategory {
  slug: string;
  title: string;
  sort_order: number;
}

export interface BlogCatalog {
  posts: BlogCatalogPost[];
  categories: BlogCatalogCategory[];
}

export interface BlogArticle {
  id: number;
  slug: string;
  locale: string;
  title: string;
  page_title: string;
  description: string | null;
  cover_image: string | null;
  category: string | null;
  category_title: string | null;
  published_at: string | null;
  html: string;
  headings: BlogHeading[];
  translations: Record<string, string>;
}

interface ArticleResponse extends BackendResponse {
  article: BlogArticle;
}

interface BlogLoader {
  locale?: string;
  catalog?: BlogCatalog;
  article?: BlogArticle | null;
  isPreview?: boolean;
  previewArticles?: Record<string, BlogArticle>;
}

export function getPublicBlogLoader(): BlogLoader | undefined {
  return getBootstrapData().loaders?.publicBlog as BlogLoader | undefined;
}

function getLoader(): BlogLoader | undefined {
  return getPublicBlogLoader();
}

function bootstrapLocale(): string {
  return getLoader()?.locale ?? 'en';
}

export function usePublicBlogCatalog() {
  const locale = bootstrapLocale();
  return useQuery({
    queryKey: ['public-blog', 'catalog', locale],
    queryFn: () =>
      apiClient
        .get('blog', {params: {locale}})
        .then(r => r.data as BlogCatalog),
    initialData: () => getLoader()?.catalog,
    staleTime: 60_000,
  });
}

export function usePublicBlogArticle(slug: string) {
  const locale = bootstrapLocale();
  const loader = getLoader();
  const cached =
    loader?.article && loader.article.slug === slug ? loader.article : undefined;

  const query = useQuery({
    queryKey: ['public-blog', 'article', slug],
    queryFn: () =>
      apiClient
        .get(`blog/posts/${slug}`, {params: {locale}})
        .then(r => (r.data as ArticleResponse).article),
    enabled: !!slug && !cached,
    initialData: cached,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  return useMemo(
    () => ({
      ...query,
      data: cached ?? query.data,
      isLoading: !cached && !!slug && query.isLoading && !query.data,
    }),
    [cached, slug, query],
  );
}
